import type HawkCatcher from "@hawk.so/browser";
import { isLowValueLongFrame, isPerformanceIssueTitle, isSyntheticAgent } from "./hawk-noise.js";
import { scrubForHawk, scrubUrlForHawk } from "./hawk-scrub.js";
import { createHawkThrottle } from "./hawk-throttle.js";
import { hashUserIdForHawk } from "./hawk-user-id.js";

/**
 * Фабрика браузерного кэтчера Hawk — одна на sso-web, id-web и admin.
 *
 * Три SPA собираются из одного монорепо и шлют события в ОДИН проект Hawk,
 * различаясь полем `context.app` и префиксом релиза (`sso-web@sha` и т.д.).
 * Общая фабрика гарантирует, что фильтрация шума и вырезание приватных данных
 * у них не разъедутся.
 *
 * SDK грузится ДИНАМИЧЕСКИМ импортом: при статическом импорте rolldown на
 * Vite 8 выносил CJS-хелперы в отдельный чанк и ронял приложение белым
 * экраном (инцидент Hero 2026-08-20). Цена — окно в сотни миллисекунд до
 * готовности катчера; его закрывает буфер ранних ошибок (`captureEarlyErrors`).
 */

const NOISE_PATTERNS = [
	"failed to fetch dynamically imported module",
	"error loading dynamically imported module",
	"chunkloaderror",
	"importing a module script failed",
	"aborterror",
	"the operation was aborted",
	"request aborted",
	"failed to fetch",
	"load failed",
	"networkerror when attempting to fetch resource",
	"network request failed",
	"resizeobserver loop",
	"script error.",
	"chrome-extension://",
	"moz-extension://",
	"safari-extension://",
	"non-error promise rejection captured",
] as const;

const MAX_EVENTS_PER_MINUTE = 30;
const DEDUPE_WINDOW_MS = 30_000;

export type HawkBrowserApp = "sso-web" | "id-web" | "admin";

export interface BrowserHawkOptions {
	/** Интеграционный токен проекта. Без него кэтчер не создаётся. */
	token: string | undefined;
	/** Какое из приложений монорепо шлёт событие. */
	app: HawkBrowserApp;
	/** Идентификатор релиза, тот же, что у загруженных sourcemap'ов. */
	release: string;
	/** Общий контекст, добавляемый к каждому событию. */
	context?: Record<string, unknown>;
	/**
	 * Дополнительный фильтр шума поверх общего списка.
	 * Получает заголовок события в нижнем регистре, `true` — выбросить.
	 */
	isNoise?: (title: string) => boolean;
}

type HawkEventContext = Parameters<HawkCatcher["send"]>[1];

export interface BrowserHawk {
	send(error: Error | string, context?: Record<string, unknown>): void;
	setUser(userId: string): void;
	clearUser(): void;
}

function isNoiseTitle(title: string): boolean {
	const normalized = title.toLowerCase();
	return NOISE_PATTERNS.some((pattern) => normalized.includes(pattern));
}

function isSyntheticClient(): boolean {
	if (typeof navigator === "undefined") return false;
	return isSyntheticAgent(navigator.userAgent, navigator.webdriver === true);
}

/**
 * Создаёт и настраивает кэтчер. Возвращает `null`, если трекинг выключен
 * (нет токена) — вызывающий код в этом случае работает как раньше.
 */
export async function createBrowserHawk(options: BrowserHawkOptions): Promise<BrowserHawk | null> {
	if (!options.token) return null;

	const allow = createHawkThrottle({
		maxPerMinute: MAX_EVENTS_PER_MINUTE,
		dedupeWindowMs: DEDUPE_WINDOW_MS,
	});

	const { default: Catcher } = await import("@hawk.so/browser");

	const catcher: HawkCatcher = new Catcher({
		token: options.token,
		release: options.release,
		context: {
			app: options.app,
			...options.context,
		},
		consoleTracking: true,
		breadcrumbs: {
			maxBreadcrumbs: 25,
			trackFetch: true,
			trackNavigation: true,
			trackClicks: true,
			beforeBreadcrumb(breadcrumb) {
				const data = breadcrumb.data as Record<string, unknown> | undefined;
				if (!data) return breadcrumb;
				if (typeof data.url === "string") {
					data.url = scrubUrlForHawk(data.url);
				}
				breadcrumb.data = scrubForHawk(data) as typeof breadcrumb.data;
				return breadcrumb;
			},
		},
		issues: {
			errors: true,
			// LCP уезжает при >5 с (дефолт SDK). Это не падение JS, а метрика;
			// в Garage нужны Grouping Patterns `Web Vitals.+` (docs/ERROR-TRACKING.md).
			webVitals: true,
			// Пороги выше дефолтных (100/300 мс): иначе лента — поток «подвисаний».
			longTasks: { thresholdMs: 500 },
			longAnimationFrames: { thresholdMs: 500 },
		},
		beforeSend(event) {
			const title = typeof event.title === "string" ? event.title : "";
			if (isNoiseTitle(title)) return false;
			if (options.isNoise?.(title.toLowerCase())) return false;
			if (isPerformanceIssueTitle(title)) {
				// Метрика описывает не код, а условия, в которых он выполнялся:
				// у краулера и headless-монитора это чужое железо на чужом канале.
				// Ошибку от них принимаем (дефект есть дефект), измерение — нет.
				if (isSyntheticClient()) return false;
				if (isLowValueLongFrame(title, event.addons)) return false;
			}
			if (typeof navigator !== "undefined" && navigator.onLine === false) {
				return false;
			}
			if (!allow(title)) return false;
			if (typeof event.context === "object" && event.context !== null) {
				event.context = scrubForHawk(event.context) as typeof event.context;
			}
			return event;
		},
	});

	return {
		send(error, context) {
			try {
				catcher.send(error, context ? (scrubForHawk(context) as HawkEventContext) : undefined);
			} catch {
				// Трекер ошибок не имеет права ронять приложение.
			}
		},
		setUser(userId) {
			void hashUserIdForHawk(userId)
				.then((hashed) => {
					if (hashed) catcher.setUser({ id: hashed });
				})
				.catch(() => undefined);
		},
		clearUser() {
			try {
				catcher.clearUser();
			} catch {
				// см. выше
			}
		},
	};
}

export interface EarlyError {
	error: Error | string;
	context: Record<string, unknown>;
}

/**
 * Ловит ошибки, пока SDK ещё грузится динамическим импортом.
 *
 * @returns функция, снимающая слушателей и отдающая накопленное.
 */
export function captureEarlyErrors(): () => EarlyError[] {
	if (typeof window === "undefined") return () => [];

	const buffer: EarlyError[] = [];

	const onError = (event: ErrorEvent): void => {
		buffer.push({
			error: event.error instanceof Error ? event.error : event.message,
			context: { source: "early-window-error", filename: event.filename },
		});
	};

	const onRejection = (event: PromiseRejectionEvent): void => {
		const reason: unknown = event.reason;
		buffer.push({
			error: reason instanceof Error ? reason : `Unhandled rejection: ${String(reason)}`,
			context: { source: "early-unhandled-rejection" },
		});
	};

	window.addEventListener("error", onError);
	window.addEventListener("unhandledrejection", onRejection);

	return () => {
		window.removeEventListener("error", onError);
		window.removeEventListener("unhandledrejection", onRejection);
		return buffer.slice(0, 5);
	};
}
